/**
 * Error handling utilities
 * Type-safe error extraction from API responses
 */

/**
 * API error response structure
 */
export interface ApiErrorData {
  success?: boolean;
  error?: {
    message?: string;
    code?: string;
    /** Sent with PAYMENT_ALREADY_SETTLED: what the provider says the payment is. */
    status?: string;
    /** Sent with LESSON_PLAN_EXISTS: the plan already on that day/period. */
    existing_plan_id?: string;
  } | string;
  message?: string;
  /** Problem-details responses (e.g. "account locked") explain here. */
  detail?: string;
}

/**
 * Extended error type for API errors
 */
export interface ApiError extends Error {
  response?: {
    data?: ApiErrorData;
    status?: number;
  };
  isApiError?: boolean;
}

/**
 * True when the API client's response interceptor has NOT already shown a
 * toast for this error. The interceptor toasts every failed request except
 * 401 / 403 / 404 (and isApiError / _skipToast requests), so a caller that
 * toasts too shows the same message twice. Callers should toast only when
 * this returns true.
 */
export function needsOwnErrorToast(error: unknown): boolean {
  const apiError = error as ApiError;
  if (apiError?.isApiError) return true;
  const status = apiError?.response?.status;
  // 401 is handled by the session logic (refresh / "Session expired").
  return status === 403 || status === 404;
}

/**
 * Extract error message from an API error
 * Handles various error response formats from the API
 *
 * @param error - The error object (can be unknown, Error, AxiosError, or ApiError)
 * @param defaultMessage - Default message if no specific error message found
 * @returns The extracted error message
 */
export function getApiErrorMessage(
  error: unknown,
  defaultMessage = "An unexpected error occurred"
): string {
  if (!error) {
    return defaultMessage;
  }

  // Handle ApiError/AxiosError with response data
  const apiError = error as ApiError;
  const errorData = apiError.response?.data;

  if (errorData) {
    // Handle { error: { message: "..." } }
    if (typeof errorData.error === "object" && errorData.error?.message) {
      return errorData.error.message;
    }

    // Handle { error: "..." }
    if (typeof errorData.error === "string") {
      return errorData.error;
    }

    // Handle { message: "..." }
    if (errorData.message) {
      return errorData.message;
    }

    // Handle problem details { detail: "..." }
    if (errorData.detail) {
      return errorData.detail;
    }
  }

  // Handle standard Error object
  if (error instanceof Error && error.message) {
    return error.message;
  }

  // Handle string error
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
}

/**
 * Check if an error is an API error with a specific status code
 */
export function isApiErrorWithStatus(
  error: unknown,
  status: number
): boolean {
  const apiError = error as ApiError;
  return apiError.response?.status === status;
}

/**
 * Check if error is a 404 Not Found
 */
export function isNotFoundError(error: unknown): boolean {
  return isApiErrorWithStatus(error, 404);
}

/**
 * Check if error is a 403 Forbidden
 */
export function isForbiddenError(error: unknown): boolean {
  return isApiErrorWithStatus(error, 403);
}

/**
 * Check if error is a 401 Unauthorized
 */
export function isUnauthorizedError(error: unknown): boolean {
  return isApiErrorWithStatus(error, 401);
}

/**
 * Check if error is a validation error (400 or 422)
 */
export function isValidationError(error: unknown): boolean {
  return (
    isApiErrorWithStatus(error, 400) || isApiErrorWithStatus(error, 422)
  );
}

/**
 * Get error code from API error response
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!error) return undefined;

  const apiError = error as ApiError;
  const errorData = apiError.response?.data;

  if (errorData && typeof errorData.error === "object") {
    return errorData.error?.code;
  }

  return undefined;
}

/**
 * The API refused to create (or move) a lesson plan because that class +
 * day + period already has one of the teacher's plans. Carries its id.
 */
export const LESSON_PLAN_EXISTS = "LESSON_PLAN_EXISTS";

/** The id of the plan already on that slot, or null if this isn't that 409. */
export function getExistingLessonPlanId(error: unknown): string | null {
  if (getApiErrorCode(error) !== LESSON_PLAN_EXISTS) return null;
  const errorData = (error as ApiError).response?.data;
  const id =
    typeof errorData?.error === "object" ? errorData.error?.existing_plan_id : undefined;
  return id ?? null;
}

/**
 * Get HTTP status code from API error response
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (!error) return undefined;

  const apiError = error as ApiError;
  return apiError.response?.status;
}

/**
 * Check if error is a 409 Conflict
 */
export function isConflictError(error: unknown): boolean {
  return isApiErrorWithStatus(error, 409);
}

/**
 * Pending payment details returned from a 409 DUPLICATE_PAYMENT response
 */
export interface PendingPaymentDetails {
  id: string;
  amount: string;
  status: string;
  created_at: string | null;
}

/**
 * Extract pending payment details from a 409 DUPLICATE_PAYMENT error.
 * Returns null if the error is not a duplicate payment conflict.
 */
export function getDuplicatePaymentDetails(
  error: unknown
): PendingPaymentDetails | null {
  if (!isConflictError(error)) return null;

  const apiError = error as ApiError;
  const errorData = apiError.response?.data;

  if (
    errorData &&
    typeof errorData.error === "object" &&
    errorData.error?.code === "DUPLICATE_PAYMENT"
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pending = (errorData as any).error?.pending_payment;
    if (pending && typeof pending.id === "string") {
      return pending as PendingPaymentDetails;
    }
  }

  return null;
}

/**
 * Which blocking payment the page may clear on its own after a 409.
 *
 * Each of these three has a Stripe PaymentIntent behind it, so the server can
 * ask Stripe whether money moved before it cancels — and it refuses if so.
 * "pending" has no Stripe intent yet (milliseconds old on another device, or
 * a failed create that expires on its own) and "processing" is money in
 * transit. Neither can be verified, so neither is touched.
 */
export function canAutoCancelPendingPayment(status: string | undefined): boolean {
  return (
    status === "requires_payment_method" ||
    status === "requires_confirmation" ||
    status === "requires_action"
  );
}

/**
 * The API refused to cancel a payment because the provider reports the money
 * already moved ("succeeded") or moving ("processing"). The server corrects
 * its own record; the page's job is to tell the family the truth rather than
 * "Payment failed".
 */
export const PAYMENT_ALREADY_SETTLED = "PAYMENT_ALREADY_SETTLED";

/** The settled status behind a PAYMENT_ALREADY_SETTLED refusal, if that is what this is. */
export function getSettledPaymentStatus(
  error: unknown
): "succeeded" | "processing" | null {
  if (getApiErrorCode(error) !== PAYMENT_ALREADY_SETTLED) return null;
  const errorData = (error as ApiError).response?.data;
  const status = typeof errorData?.error === "object" ? errorData.error?.status : undefined;
  // Only the two statuses the server sends. A refusal without one is not
  // a licence to announce "paid" for money that may only be processing.
  if (status === "succeeded" || status === "processing") return status;
  return null;
}

/**
 * Extract user-friendly decline reason from a CARD_DECLINED error.
 * Returns null if the error is not a card decline.
 */
export function getDeclineReason(
  error: unknown
): { message: string; decline_code: string | null } | null {
  const apiError = error as ApiError;
  const errorData = apiError.response?.data;

  if (
    errorData &&
    typeof errorData.error === "object" &&
    errorData.error?.code === "CARD_DECLINED"
  ) {
    return {
      message: errorData.error.message || "Your card was declined.",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      decline_code: (errorData.error as any).decline_code || null,
    };
  }

  return null;
}
